import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Grimoire from './components/Grimoire';
import Spells from './components/Spells';
import PublicFeed from './components/PublicFeed';
import Profile from './components/Profile';
import LunarTracker from './components/LunarTracker';
import ReferenceLibrary from './components/ReferenceLibrary';
import ReferenceDetail from './components/ReferenceDetail';
import RichTextEditor from './components/RichTextEditor';
import TarotReader from './components/TarotReader';
import SharedItemDetail from './components/SharedItemDetail';
import Messages from './components/Messages';
import AdminConsole from './components/AdminConsole';
import LandingPage from './components/LandingPage';

// Components
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FeedbackModal = ({ user, onClose }: any) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/feedback`, {
        user_id: user?.id || null,
        content
      });
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      console.error('Failed to submit feedback', err);
      alert('Failed to send feedback.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <h3>Share your Whispers</h3>
        {success ? (
          <p style={{ color: 'var(--gold)' }}>Your feedback has been received. The ancients thank you.</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <textarea 
              value={content} 
              onChange={e => setContent(e.target.value)} 
              placeholder="Tell us about your experience, bugs found, or features desired..."
              required
              rows={5}
              style={{ width: '100%', background: '#111', color: 'white', border: '1px solid #333', borderRadius: '4px', padding: '0.5rem', marginBottom: '1rem' }}
            />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" disabled={loading}>{loading ? 'Sending...' : 'Send Feedback'}</button>
              <button type="button" onClick={onClose} style={{ backgroundColor: '#444' }}>Close</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Navbar = ({ user, onLogout, unreadCount }: any) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowDropdown(false);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <>
    <nav>
      <Link to="/"><h1>Starlit Grimoire</h1></Link>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Link to="/references">Reference Library</Link>
        <Link to="/lunar-tracker">Moon</Link>
        {user ? (
          <>
            <Link to="/public">Community Feed</Link>
            <Link to="/tarot-reader">Tarot Reader</Link>
            <Link to="/journal">Journal</Link>
            <Link to="/grimoire">Grimoire</Link>
            <Link to="/spells">Spell Tracker</Link>
            {user.is_admin === 1 && <Link to="/admin" style={{ color: 'var(--gold)', fontWeight: 'bold' }}>Admin</Link>}
            <div className="profile-dropdown" onClick={(e) => e.stopPropagation()}>
              <div className="profile-trigger" onClick={() => setShowDropdown(!showDropdown)}>
                {user.profile_picture ? (
                  <img src={user.profile_picture} alt={user.username} />
                ) : (
                  <div className="initials">{user.username[0].toUpperCase()}</div>
                )}
              </div>
              <div className={`dropdown-menu ${showDropdown ? 'show' : ''}`}>
                <Link to="/profile" onClick={() => setShowDropdown(false)}>Your Profile</Link>
                <Link to="/messages" onClick={() => setShowDropdown(false)}>
                  Messages {unreadCount > 0 && <span className="badge" style={{ backgroundColor: 'var(--gold)', color: 'black', padding: '0.1rem 0.4rem', borderRadius: '50%', fontSize: '0.7rem' }}>{unreadCount}</span>}
                </Link>
                <button onClick={() => setShowFeedback(true)}>Send Feedback</button>
                <button onClick={() => { onLogout(); setShowDropdown(false); }}>Logout</button>
              </div>
            </div>
          </>
        ) : (
          <>
            <Link to="/auth">Login / Register</Link>
          </>
        )}
      </div>
    </nav>
    {showFeedback && <FeedbackModal user={user} onClose={() => setShowFeedback(false)} />}
    </>
  );
};

const Auth = ({ onLogin }: any) => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? '/login' : '/register';
      const payload = isLogin 
        ? { username, password } 
        : { username, password, first_name: firstName, last_name: lastName, email, display_name: displayName };
        
      const res = await axios.post(`${API_URL}${endpoint}`, payload);
      if (isLogin) {
        onLogin(res.data);
      } else {
        setIsLogin(true);
        setError('Registered successfully. Please login.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong');
    }
  };

  return (
    <div className="content" style={{ maxWidth: '400px' }}>
      <h2>{isLogin ? 'Login to your Circle' : 'Join the Circle'}</h2>
      {error && <p style={{ color: 'var(--gold)' }}>{error}</p>}
      <form onSubmit={handleSubmit}>
        {!isLogin && (
          <>
            <input type="text" placeholder="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} required />
            <input type="text" placeholder="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} required />
            <input type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} required />
            <input type="text" placeholder="Display Name (Optional)" value={displayName} onChange={e => setDisplayName(e.target.value)} />
          </>
        )}
        <input type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} required />
        <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
        <button type="submit">{isLogin ? 'Login' : 'Register'}</button>
      </form>
      <p onClick={() => setIsLogin(!isLogin)} style={{ cursor: 'pointer', marginTop: '1rem' }}>
        {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
      </p>
    </div>
  );
};

const Journal = ({ user }: any) => {
  const location = useLocation();
  const [journal, setJournal] = useState([]);
  const [content, setContent] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [lunarData, setLunarData] = useState<any>(null);
  const [includeLunar, setIncludeLunar] = useState(false);
  const [isPrivate, setIsPrivate] = useState(true);

  useEffect(() => {
    if (location.state?.prefillJournal) {
      setContent(location.state.prefillJournal);
      // Clear state so it doesn't prefill again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchJournal = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/journal`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setJournal(res.data);
    } catch (err) {
      console.error('Failed to fetch journal', err);
    }
  }, [user.token]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const lunarRes = await axios.get(`${API_URL}/lunar-phase`);
        setLunarData(lunarRes.data);
        fetchJournal();
      } catch (err) {
        console.error('Failed to fetch journal data', err);
      }
    };
    fetchInitialData();
  }, [user, fetchJournal]);

  const addEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    const today = new Date().toISOString().split('T')[0];
    const payload = { 
        date: today, 
        content, 
        is_private: isPrivate ? 1 : 0,
        lunar_phase: includeLunar ? lunarData?.phase_name : null
    };

    try {
      if (editingId) {
        await axios.put(`${API_URL}/journal/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        setEditingId(null);
      } else {
        await axios.post(`${API_URL}/journal`, payload, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      }
      setContent('');
      setIncludeLunar(false);
      setIsPrivate(true);
      fetchJournal();
    } catch (err) {
      console.error('Failed to save journal entry', err);
      alert('Failed to save journal entry.');
    }
  };

  const togglePrivacy = async (entry: any) => {
    try {
      await axios.put(`${API_URL}/journal/${entry.id}`, {
        ...entry,
        is_private: entry.is_private ? 0 : 1
      }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      fetchJournal();
    } catch (err) {
      console.error('Failed to toggle privacy', err);
    }
  };

  const startEdit = (entry: any) => {
    setEditingId(entry.id);
    setContent(entry.content);
    setIncludeLunar(!!entry.lunar_phase);
    setIsPrivate(!!entry.is_private);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteEntry = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this journal entry?')) {
      try {
        await axios.delete(`${API_URL}/journal/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchJournal();
      } catch (err) {
        console.error('Failed to delete journal entry', err);
        alert('Failed to delete entry.');
      }
    }
  };

  return (
    <div className="content">
      <h2>Your Journal</h2>
      
      {lunarData && (
        <div className="lunar-widget card" style={{ cursor: 'pointer' }} onClick={() => window.location.href='/lunar-tracker'}>
          <div className="lunar-info">
            <span className="lunar-emoji">{lunarData.emoji}</span>
            <div>
              <h3>Current Lunar Phase</h3>
              <p><strong>{lunarData.phase_name}</strong> in {lunarData.zodiac_sign || '...'} </p>
              <p>Illumination: {lunarData.illumination}% | Age: {Math.round(lunarData.age)} days</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h3>{editingId ? 'Edit Journal Entry' : 'New Daily Journal Entry'}</h3>
        <form onSubmit={addEntry}>
          <RichTextEditor content={content} onChange={setContent} placeholder="Write your thoughts..." />
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={includeLunar} onChange={e => setIncludeLunar(e.target.checked)} style={{ width: 'auto', marginBottom: 0 }} />
                <span>Include Moon Phase ({lunarData?.phase_name})</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} style={{ width: 'auto', marginBottom: 0 }} />
                <span>Private Entry (Uncheck to share with community)</span>
            </label>
          </div>
          <button type="submit">{editingId ? 'Update Entry' : 'Save Entry'}</button>
          {editingId && (
            <button 
              type="button" 
              onClick={() => { setEditingId(null); setContent(''); setIncludeLunar(false); setIsPrivate(true); }}
              style={{ marginLeft: '1rem', backgroundColor: '#666' }}
            >
              Cancel
            </button>
          )}
        </form>
      </div>
      <h3>Recent Journal Entries</h3>
      {journal.map((entry: any) => (
        <div key={entry.id} className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <small>{entry.date} {entry.is_private ? '🔒 Private' : '🌐 Public'}</small>
                <div style={{ marginTop: '0.3rem' }}>
                    {entry.lunar_phase && <span className="badge" style={{ fontSize: '0.7rem' }}>🌙 {entry.lunar_phase}</span>}
                </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                    onClick={() => togglePrivacy(entry)}
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem', backgroundColor: entry.is_private ? 'var(--accent-purple)' : '#444' }}
                >
                    {entry.is_private ? 'Share' : 'Make Private'}
                </button>
                <button 
                    onClick={() => startEdit(entry)}
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                >
                    Edit
                </button>
                <button 
                    onClick={() => deleteEntry(entry.id)}
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#8b0000' }}
                >
                    Delete
                </button>
            </div>
          </div>
          <div className="rich-content" style={{ marginTop: '1rem' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.content) }} />
        </div>
      ))}
    </div>
  );
};

function App() {
  const [user, setUser] = useState<any>(() => {
    const savedUser = localStorage.getItem('magic_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUserProfile = useCallback(async (token: string) => {
    try {
      const res = await axios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser((prev: any) => ({ ...prev, ...res.data }));
      // Also update localStorage
      const savedUser = JSON.parse(localStorage.getItem('magic_user') || '{}');
      localStorage.setItem('magic_user', JSON.stringify({ ...savedUser, ...res.data }));
    } catch (err) {
      console.error('Failed to fetch user profile details', err);
    }
  }, []);

  const fetchUnreadCount = useCallback(async (token: string) => {
    try {
      const res = await axios.get(`${API_URL}/messages-unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error('Failed to fetch unread count', err);
    }
  }, []);

  useEffect(() => {
    const savedUser = localStorage.getItem('magic_user');
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      setUser(parsed);
      fetchUserProfile(parsed.token);
      fetchUnreadCount(parsed.token);
      
      // Poll for unread messages every minute
      const interval = setInterval(() => fetchUnreadCount(parsed.token), 60000);
      return () => clearInterval(interval);
    }
  }, [fetchUserProfile, fetchUnreadCount]);

  const handleLogin = (userData: any) => {
    setUser(userData);
    localStorage.setItem('magic_user', JSON.stringify(userData));
    fetchUserProfile(userData.token);
    fetchUnreadCount(userData.token);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('magic_user');
  };

  return (
    <Router>
      <div className="app-container">
        <Navbar user={user} onLogout={handleLogout} unreadCount={unreadCount} />
        <Routes>
          <Route path="/" element={<LandingPage user={user} />} />
          <Route path="/public" element={user ? <PublicFeed user={user} /> : <Navigate to="/auth" />} />
          <Route path="/public/:type/:id" element={user ? <SharedItemDetail user={user} /> : <Navigate to="/auth" />} />
          <Route path="/messages" element={user ? <Messages user={user} /> : <Navigate to="/auth" />} />
          <Route path="/messages/:username" element={user ? <Messages user={user} /> : <Navigate to="/auth" />} />
          <Route path="/references" element={<ReferenceLibrary />} />
          <Route path="/references/:id" element={<ReferenceDetail user={user} />} />
          <Route path="/tarot-reader" element={user ? <TarotReader user={user} /> : <Navigate to="/auth" />} />
          <Route path="/lunar-tracker" element={<LunarTracker />} />
          <Route path="/admin" element={user?.is_admin ? <AdminConsole user={user} /> : <Navigate to="/" />} />
          <Route path="/auth" element={!user ? <Auth onLogin={handleLogin} /> : <Navigate to="/" />} />
          <Route path="/journal" element={user ? <Journal user={user} /> : <Navigate to="/auth" />} />
          <Route path="/profile" element={user ? <Profile user={user} refreshUser={() => fetchUserProfile(user.token)} /> : <Navigate to="/auth" />} />
          <Route path="/profile/:username" element={<Profile user={user} refreshUser={() => user && fetchUserProfile(user.token)} />} />
          <Route path="/grimoire" element={user ? <Grimoire user={user} /> : <Navigate to="/auth" />} />
          <Route path="/spells" element={user ? <Spells user={user} /> : <Navigate to="/auth" />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
